/**
 * Vehicle List Component
 * Task 57: Vehicle management for auto repair shops
 */

import { useState, useMemo } from 'react';
import { IndustryVehicle } from '@/types/industry';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { MoreHorizontal, Pencil, Trash2, Plus, Search, Car } from 'lucide-react';

interface VehicleListProps {
  vehicles: IndustryVehicle[];
  onEdit?: (vehicle: IndustryVehicle) => void;
  onDelete?: (vehicle: IndustryVehicle) => void;
  onCreate?: () => void;
}

export function VehicleList({ vehicles, onEdit, onDelete, onCreate }: VehicleListProps) {
  const [searchTerm, setSearchTerm] = useState('');

  // Filter vehicles
  const filteredVehicles = useMemo(() => {
    return vehicles.filter((vehicle) => {
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        const matchesPlate = vehicle.metadata?.plate?.toString().toLowerCase().includes(term);
        const matchesMake = vehicle.metadata?.make?.toString().toLowerCase().includes(term);
        const matchesModel = vehicle.metadata?.model?.toString().toLowerCase().includes(term);
        const matchesClient = vehicle.metadata?.clientName?.toString().toLowerCase().includes(term);
        if (!matchesPlate && !matchesMake && !matchesModel && !matchesClient) return false;
      }
      return true;
    });
  }, [vehicles, searchTerm]);

  return (
    <div className="space-y-4">
      {/* Header */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Car className="h-5 w-5" />
                Veículos
              </CardTitle>
              <CardDescription>Cadastro de veículos dos clientes</CardDescription>
            </div>
            {onCreate && (
              <Button onClick={onCreate}>
                <Plus className="mr-2 h-4 w-4" />
                Novo Veículo
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {/* Search */}
          <div className="max-w-sm">
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por placa, modelo, cliente..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-8"
              />
            </div>
          </div>

          {/* Results Count */}
          <div className="mt-4 text-sm text-muted-foreground">
            Mostrando {filteredVehicles.length} de {vehicles.length} veículos
          </div>
        </CardContent>
      </Card>

      {/* Vehicles Table */}
      <Card>
        <CardContent className="pt-6">
          {filteredVehicles.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              {searchTerm
                ? 'Nenhum veículo encontrado com os filtros selecionados'
                : 'Nenhum veículo cadastrado'}
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Placa</TableHead>
                    <TableHead>Veículo</TableHead>
                    <TableHead>Ano</TableHead>
                    <TableHead>Cliente</TableHead>
                    <TableHead>VIN/Chassi</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredVehicles.map((vehicle) => {
                    const plate = vehicle.metadata?.plate as string;
                    const make = vehicle.metadata?.make as string;
                    const model = vehicle.metadata?.model as string;
                    const year = vehicle.metadata?.year as number;
                    const vin = vehicle.metadata?.vin as string;
                    const clientName = vehicle.metadata?.clientName as string;

                    return (
                      <TableRow key={vehicle.id}>
                        <TableCell>
                          <div className="font-medium font-mono">{plate || '-'}</div>
                        </TableCell>
                        <TableCell>
                          <div>
                            {make && model ? (
                              <>
                                <div className="font-medium">{make}</div>
                                <div className="text-sm text-muted-foreground">{model}</div>
                              </>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>{year || '-'}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {clientName || '-'}
                        </TableCell>
                        <TableCell className="text-sm font-mono">
                          {vin || '-'}
                        </TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {onEdit && (
                                <DropdownMenuItem onClick={() => onEdit(vehicle)}>
                                  <Pencil className="mr-2 h-4 w-4" />
                                  Editar
                                </DropdownMenuItem>
                              )}
                              {onDelete && (
                                <DropdownMenuItem
                                  onClick={() => onDelete(vehicle)}
                                  className="text-destructive"
                                >
                                  <Trash2 className="mr-2 h-4 w-4" />
                                  Excluir
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
